import { useRef, useState } from 'react'

import { Box, Button, Card, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0TagRaw } from '@factorialco/f0-react'
import { Link, LinkRemove } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import i18n from 'modules/core/lib/i18n'

import RatesSingleForm from 'modules/project_management/components/ProfitabilityCards/RatesSingleForm'
import {
  calculateBillRateFromMarkup,
  calculateMarkupPercentage,
  calculateMarginPercentage,
  convertCentsToPercentage,
} from 'modules/project_management/lib/profitabilityCalculations'

interface EditRateCardProps {
  areJobLevelRates?: boolean
  areProjectWorkerRates?: boolean
  billingRateCents: number
  currency: string
  referenceBillingRate?: number
  referenceProfitMargin?: number
  maxCostRate: number
  initialProfitMarginCents: number
  onClose?: () => void
  onSubmit: (values: { billRate: string; minProfitMargin: string }) => void
}

const EditRateCard = ({
  areJobLevelRates = false,
  areProjectWorkerRates = false,
  billingRateCents,
  currency,
  referenceBillingRate,
  referenceProfitMargin,
  maxCostRate,
  initialProfitMarginCents,
  onClose,
  onSubmit,
}: EditRateCardProps) => {
  const [errors, setErrors] = useState<{ billRate?: string; minProfitMargin?: string }>({})

  const calculateMarkup = (maxCostRateValue: number, billRateValue: string): string => {
    const bill = parseFloat(billRateValue) || 0
    return calculateMarkupPercentage(maxCostRateValue, bill)
  }

  const initialProfitMarginValue = convertCentsToPercentage(initialProfitMarginCents).toString()
  const [minProfitMargin, setMinProfitMargin] = useState<string>(initialProfitMarginValue)

  const lastUpdatedField = useRef<'profitMargin' | 'billRate' | null>(null)

  const calculateBillRateFromMarkupInput = (
    maxCostRateValue: number,
    markupValue: string
  ): string => {
    const parsedMarkup = parseFloat(markupValue) || 0
    return calculateBillRateFromMarkup(maxCostRateValue, parsedMarkup)
  }

  const initialBillRateValue = billingRateCents
    ? billingRateCents.toString()
    : calculateBillRateFromMarkupInput(maxCostRate, initialProfitMarginValue)

  const [billRate, setBillRate] = useState<string>(initialBillRateValue)

  const currentBillRate = parseFloat(billRate) || 0
  const currentProfitMargin = parseFloat(minProfitMargin) || 0
  const initialBillRate = parseFloat(initialBillRateValue) || 0
  const initialProfit = parseFloat(initialProfitMarginValue) || 0

  const hasInitialValuesChanged =
    currentBillRate !== initialBillRate || currentProfitMargin !== initialProfit

  const handleBillRateChange = (value: string) => {
    lastUpdatedField.current = 'billRate'
    setBillRate(value)

    if (+value <= 0 || value === '') {
      setErrors({ ...errors, billRate: i18n.t('common.invalid_value') })
    } else {
      setErrors({ ...errors, billRate: undefined })
    }

    const newMarkup = calculateMarkup(maxCostRate, value)
    setMinProfitMargin(newMarkup)
  }

  const handleProfitMarginChange = (value: string) => {
    lastUpdatedField.current = 'profitMargin'
    setMinProfitMargin(value)

    const newBillRate = calculateBillRateFromMarkupInput(maxCostRate, value)
    setBillRate(newBillRate)

    if (+newBillRate <= 0 || newBillRate === '') {
      setErrors((prev) => ({ ...prev, billRate: i18n.t('common.invalid_value') }))
    } else {
      setErrors((prev) => ({ ...prev, billRate: undefined }))
    }
  }

  const handleSubmit = () => {
    onSubmit({ billRate, minProfitMargin })
    onClose?.()
  }

  const alertTitle = areJobLevelRates
    ? i18n.t('project_management.jobs_and_rates.sidepanels.alerts.not_affect_projects.title')
    : areProjectWorkerRates
      ? i18n.t('project_management.project_workers.details.alerts.rate_changed.title')
      : i18n.t('project_management.people_and_rates.sidepanel.alerts.setting_custom_rate.title')
  const alertDescription = areJobLevelRates
    ? i18n.t('project_management.jobs_and_rates.sidepanels.alerts.not_affect_projects.description')
    : areProjectWorkerRates
      ? i18n.t('project_management.project_workers.details.alerts.rate_changed.description')
      : i18n.t(
          'project_management.people_and_rates.sidepanel.alerts.setting_custom_rate.description'
        )

  const notReferenceBillingRateValue = referenceBillingRate !== Number(billRate)

  const marginPercentage = calculateMarginPercentage(maxCostRate, parseFloat(billRate) || 0)

  return (
    <Card>
      <Card.Content>
        <Box width="full">
          <Box flexDirection="row" gap="s8" paddingBottom="s32">
            {areProjectWorkerRates ? (
              <F0TagRaw
                icon={referenceBillingRate === billingRateCents ? Link : LinkRemove}
                text={
                  referenceBillingRate === billingRateCents
                    ? i18n.t('project_management.project_workers.details.default_rate')
                    : i18n.t('project_management.people_and_rates.sidepanel.tags.custom')
                }
              />
            ) : (
              <Text size="300" color="grey1000" weight="semibold">
                {i18n.t('project_management.jobs_and_rates.sidepanels.new_rate_button')}
              </Text>
            )}
          </Box>
          <RatesSingleForm
            billRate={billRate}
            currency={currency}
            errors={errors}
            maxCostRate={maxCostRate.toString()}
            minProfitMargin={minProfitMargin}
            marginPercentage={marginPercentage}
            handleBillRateChange={handleBillRateChange}
            handleProfitMarginChange={handleProfitMarginChange}
            areJobLevelRates={areJobLevelRates}
          />
          {!areJobLevelRates &&
            notReferenceBillingRateValue &&
            referenceBillingRate !== undefined &&
            referenceProfitMargin !== undefined && (
              <Box
                onClick={() => {
                  setBillRate(referenceBillingRate.toString())
                  setMinProfitMargin(referenceProfitMargin.toString())
                }}
                alignSelf="center"
                paddingTop="s6"
                paddingRight="s40"
              >
                <Text underlined size="200" color="grey1000" weight="semibold">
                  {areProjectWorkerRates
                    ? i18n.t('project_management.project_workers.details.reser_to_default')
                    : i18n.t('project_management.people_and_rates.form.reset_to_job_rate')}
                </Text>
              </Box>
            )}
        </Box>
        {!areProjectWorkerRates && (
          <Box paddingTop="s20" width="full">
            <DayPicker
              readonly
              label={i18n.t(
                'project_management.jobs_and_rates.sidepanels.form.efective_dates.label'
              )}
              value={moment(new Date()).format('L')}
              onChange={() => {}}
            />
          </Box>
        )}

        {notReferenceBillingRateValue && (
          <Box paddingTop="s24" width="full">
            <F0Alert
              variant={areJobLevelRates ? 'info' : 'warning'}
              title={alertTitle}
              description={alertDescription}
            />
          </Box>
        )}

        <Box flexDirection="row" gap="s8" paddingBottom="s8" paddingTop="s32" alignSelf="flexEnd">
          {onClose && (
            <Button type="secondary" onClick={onClose}>
              {i18n.t('common.cancel')}
            </Button>
          )}
          <Button disabled={!hasInitialValuesChanged || !!errors.billRate} onClick={handleSubmit}>
            {i18n.t('common.save_changes')}
          </Button>
        </Box>
      </Card.Content>
    </Card>
  )
}

export default EditRateCard
